import { useState, useEffect } from "react";
import {
  Button,
  Box,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Stack,
  Text,
  useColorModeValue,
  useToast,
  Tag,
  TagLabel,
  TagCloseButton,
  Heading,
  Wrap,
  useColorMode,
} from "@chakra-ui/react";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
// API requests
import {
  getZohoClients,
  getTaskList,
  createClientProject,
} from "api/requests/zohoRequests";
import { updateClient } from "api/requests/updateClient";
import { addClient } from "api/requests/addClient";
import { getClients } from "api/requests/getClients.js";
// AWS SDK
import AWS from "aws-sdk";
import { Card, Modal, useMantineTheme, Group } from "@mantine/core";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Select from "react-select";
import Fuse from "fuse.js";

import useAuth from "hooks/useAuth";
import useAxiosPrivate from "hooks/useAxiosPrivate";

function NewClient() {
  const MySwal = withReactContent(Swal);
  const axiosPrivate = useAxiosPrivate();
  // colors
  const { colorMode } = useColorMode();
  const bgColor = useColorModeValue("white", "#4A5058");
  const textColor = useColorModeValue("gray.600", "white");

  const [isLoading, setIsLoading] = useState(false);
  const [newTitles, setNewTitles] = useState("empty");
  const [zohoClients, setZohoClients] = useState([]);
  const newOption = zohoClients ? zohoClients : "No clients";
  const [value, setValue] = useState();
  const [client, setClient] = useState();
  const [clients, setClients] = useState([]);
  const [data, setData] = useState([]);

  const [override, setOverride] = useState(false);

  const [clientName, setClientName] = useState();
  const [clientUrl, setClientUrl] = useState();

  const [duplicate, setDuplicate] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [zohoDuplicates, setZohoDuplicates] = useState([]);
  const [dbClient, setDbClient] = useState();

  const theme = useMantineTheme();
  const [slowTransitionOpened, setSlowTransitionOpened] = useState(false);

  const [isClicked, setIsClicked] = useState(false);
  const [selectedClientIndex, setSelectedClientIndex] = useState(null);
  const toast = useToast();
  const S3_BUCKET_NAME = process.env.REACT_APP_S3_BUCKET_NAME;
  const AWS_REGION = process.env.REACT_APP_AWS_REGION;
  const S3_ACCESS_KEY_ID = process.env.REACT_APP_S3_ACCESS_KEY_ID;
  const S3_SECRET_ACCESS_KEY = process.env.REACT_APP_S3_SECRET_ACCESS_KEY;
  const [zohoTaskLists, setZohoTaskLists] = useState([]);
  const [defaultNewClientTasks, setDefaultNewClientTasks] = useState([
    {
      id: "1290214000007417867",
      name: "[ONBOARDING GENERAL]",
      status: true,
    },
  ]);

  useEffect(() => {
    if (client) {
      handleLabelChange(client.Account_Name);
      if (client.Website) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          clientwebsite: client.Website,
        }));
      }
    }
  }, [client]);

  const handleClick = (client, index) => {
    setDbClient(client);
    setIsClicked(!isClicked);
    setSelectedClientIndex(index); // Track the index of the selected client
  };

  const fetchClients = async () => {
    try {
      const data = await getClients();
      const filteredData = data.filter(
        (client) => client.status !== false && client.brand !== "AutoGo"
      );

      setClients(filteredData);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    }
  };

  const fetchZohoClients = async () => {
    try {
      const data = await getZohoClients();

      const modifiedData = data.map((client) => ({
        ...client,
        label: client.Account_Name,
        value: client.Account_Name.toString().replace(/\W/g, ""),
      }));

      setZohoClients(modifiedData);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchZohoClients();
  }, []);

  const handleOnClick = (selectedZohoClient) => {
    if (selectedZohoClient) {
      setClient(selectedZohoClient);

      const { Account_Name } = selectedZohoClient;

      // Configure Fuse.js options for clients
      const optionsClients = {
        keys: ["label"],
        threshold: 0.6, // Lower threshold for more matches
        distance: 50, // Increase distance for better matching
        tokenize: true, // Enable tokenization to match partial terms
        matchAllTokens: true, // Ensure all tokens in the query must be present
      };

      // Fuse.js instance for clients
      const fuseClients = new Fuse(clients, optionsClients);
      let matchingClients = fuseClients
        .search(Account_Name)
        .map((result) => result.item);

      // Post-processing to filter out less relevant matches
      matchingClients = matchingClients.filter(
        (client) =>
          Account_Name.toLowerCase().includes(client.label.toLowerCase()) ||
          client.label.toLowerCase().includes(Account_Name.toLowerCase())
      );

      // Configure Fuse.js options for zohoClients
      const optionsZohoClients = {
        keys: ["Account_Name"],
        threshold: 0.4,
      };

      // Fuse.js instance for zohoClients
      const fuseZohoClients = new Fuse(zohoClients, optionsZohoClients);
      let matchingZohoClients = fuseZohoClients
        .search(Account_Name)
        .map((result) => result.item);

      // Post-processing for zohoClients as well
      matchingZohoClients = matchingZohoClients.filter(
        (client) =>
          Account_Name.toLowerCase().includes(
            client.Account_Name.toLowerCase()
          ) ||
          client.Account_Name.toLowerCase().includes(Account_Name.toLowerCase())
      );

      setDuplicates(matchingClients);
      setZohoDuplicates(matchingZohoClients);

      console.log("Client Matches:", matchingClients);
      console.log("Zoho Client Matches:", matchingZohoClients);

      // Ensure setDuplicate is handled correctly
      if (matchingClients.length > 0 || matchingZohoClients.length > 0) {
        setDuplicate(true);
      } else {
        setDuplicate(false);
      }
    } else {
      setClient(null);
      setDbClient();
      setIsClicked(false);
      setDuplicate(false);
      setDuplicates([]);
      setZohoDuplicates([]);
    }
  };

  const colorStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: useColorModeValue("white", "#495057"),

      padding: "4px",
      borderRadius: "4px",
      border: "none",
    }),
    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      backgroundColor:
        isSelected && colorMode === "dark"
          ? "#3182ce"
          : isFocused
          ? "#3182ce"
          : undefined,
      color: useColorModeValue("gray.200", "white"),
    }),
    singleValue: (styles, { isFocused }) => {
      return {
        ...styles,
        color: useColorModeValue("white", "white"),
        backgroundColor:
          colorMode === "dark" ? "#3182ce" : isFocused ? "#d0dcfb" : "#3182ce",
        padding: "5px",
        borderRadius: "4px",
        fontSize: "85%",
        width: "fit-content",
      };
    },
    menu: (styles) => ({
      ...styles,
      backgroundColor: useColorModeValue("white", "#495057"),
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  // Checkboxes
  const [checkboxState, setCheckboxState] = useState({
    aiCoupon: {
      id: "1290214000007226362",
      status: false,
    },
    coop: {
      id: "1290214000007226308",
      status: false,
    },
    creativeBanners: {
      id: "1290214000007226502",
      status: false,
    },
    creativeEblast: {
      id: "1290214000007952396",
      status: false,
    },
    ga4: {
      id: "1290214000007226526",
      status: false,
    },
    googleDisplay: {
      id: "1290214000007226638",
      status: false,
    },
    googleVla: {
      id: "1290214000007226654",
      status: false,
    },
    ott: {
      id: "1290214000007226586",
      status: false,
    },
    paidSearch: {
      id: "1290214000007226754",
      status: false,
    },
    paidSocial: {
      id: "1290214000007226702",
      status: false,
    },
    coreSeo: {
      id: "1290214000010213779",
      status: false,
    },
    enhancedSeo: {
      id: "1290214000010213779",
      status: false,
    },
    premiumSeo: {
      id: "1290214000010213779",
      status: false,
    },
    serviceAdPackage: {
      id: "1290214000007226442",
      status: false,
    },
    spanishAdPackage: {
      id: "",
      status: false,
    },
    techSeo: {
      id: "1290214000007761139",
      status: false,
    },
    websiteManagement: {
      id: "1290214000007226466",
      status: false,
    },
    repMan: {
      id: "1290214000009805031",
      status: false,
    },
    coop: {
      id: "1290214000009704849",
      status: false,
    },
  });

  const [createCheckbox, setCreateCheckbox] = useState({
    database: false,
    projects: false,
  });

  const [formData, setFormData] = useState({
    value: "",
    label: "",
    s3FolderPath: "",
    products: {
      aiCoupon: {
        status: false,
      },
      automatedSpecials: {
        status: false,
      },
      coop: {
        status: false,
      },
      creativeBanners: {
        status: false,
      },
      creativeEblast: {
        status: false,
      },
      ga4: {
        status: false,
      },
      googleDisplay: {
        status: false,
      },
      googleVla: {
        status: false,
      },
      ott: {
        status: false,
      },
      paidSearch: {
        status: false,
      },
      paidSocial: {
        status: false,
      },
      paidSocial: {
        status: false,
      },
      coreSeo: {
        status: false,
      },
      enhancedSeo: {
        status: false,
      },
      premiumSeo: {
        status: false,
      },
      serviceAdPackage: {
        status: false,
      },
      spanishAdPackage: {
        status: false,
      },
      techSeo: {
        status: false,
      },
      websiteManagement: {
        status: false,
      },
    },
    hNavItems: 3,
    footerNav: false,
    imageCount: 0,
    imageLinks: [],
    clientwebsite: "",
    brand: "",
    navbar: {
      newvehicle: "",
      usedvehicle: "",
      additional: "",
      finance: "",
    },
    specials: "",
    additionaltext: "",
    status: true,
  });

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;

    // Determine if the input type is a checkbox to use `checked`, otherwise use `value`
    const inputValue = type === "checkbox" ? checked : value;

    if (name === "label") {
      setFormData((prev) => ({
        ...prev,
        label: inputValue, // Assuming you want to use the inputValue here as well
        value: inputValue.toString().replace(/\W/g, ""), // Convert boolean to string if needed, then sanitize
      }));
    } else {
      const keys = name.split("."); // Split the name by dots for nested objects
      if (keys.length === 3) {
        // Handle three levels of nesting
        setFormData((prev) => ({
          ...prev,
          [keys[0]]: {
            ...prev[keys[0]],
            [keys[1]]: {
              ...prev[keys[0]][keys[1]],
              [keys[2]]: inputValue,
            },
          },
        }));
      } else if (keys.length === 2) {
        // Handle two levels of nesting
        setFormData((prev) => ({
          ...prev,
          [keys[0]]: {
            ...prev[keys[0]],
            [keys[1]]: inputValue,
          },
        }));
      } else {
        // Handle non-nested fields
        setFormData((prev) => ({ ...prev, [name]: inputValue }));
      }
    }
  };

  const handleLabelChange = (input) => {
    const value = typeof input === "string" ? input : input.target.value;

    setFormData((prevFormData) => ({
      ...prevFormData,
      label: value,
    }));

    setFormData((prevFormData) => ({
      ...prevFormData,
      value: value.replace(/\W/g, ""),
    }));

    setFormData((prev) => ({
      ...prev,
      additionaltext: `Shop today at ${value} and save. These offers won't last long.`,
    }));
  };

  const handleSaveClientDirectory = async (clientURL, _id) => {
    const clientData = { s3FolderPath: clientURL };
    const response = await updateClient(clientData, _id);
    if (response.status === 200) {
      toast({
        title: "Success!",
        description: "Client directory attached to client.",
        status: "success",
        duration: 2000,
      });
    } else {
      toast({
        title: "Error!",
        description: `Status code: ${response.status}`,
        status: "error",
        duration: 2000,
      });
    }
  };

  const handleCreateDirectory = async (label, _id) => {
    // Configure AWS SDK with your credentials
    AWS.config.update({
      region: AWS_REGION,
      accessKeyId: S3_ACCESS_KEY_ID,
      secretAccessKey: S3_SECRET_ACCESS_KEY,
    });
    const s3 = new AWS.S3();
    // S3 copy _Empty-Product-folders to newly created client folder
    const sourceBucket = S3_BUCKET_NAME;
    const sourcePrefix = `client-creative/_Empty-Product-folders/`;
    const destinationBucket = S3_BUCKET_NAME;
    const destinationPrefix = `client-creative/${label}/`;
    const listParams = {
      Bucket: sourceBucket,
      Prefix: sourcePrefix,
    };
    const listedObjects = await s3.listObjectsV2(listParams).promise();
    if (listedObjects.Contents.length === 0) return;
    for (let object of listedObjects.Contents) {
      let destinationKey = object.Key.replace(sourcePrefix, destinationPrefix);
      await s3
        .copyObject({
          Bucket: destinationBucket,
          CopySource: `${sourceBucket}/${object.Key}`,
          Key: destinationKey,
        })
        .promise();
    }
    if (listedObjects.IsTruncated)
      await copyS3Folder(
        sourceBucket,
        sourcePrefix,
        destinationBucket,
        destinationPrefix
      );
    toast({
      title: "Success!",
      description: "Client directory created",
      status: "success",
      duration: 3000,
    });
    const clientURL = `client-creative/${label}/Emails/html-code-out-files/images/`;
    handleSaveClientDirectory(clientURL, _id);
  };

  const handleAddClient = async (data) => {
    // Check if no create option was selected
    if (!createCheckbox.database && !createCheckbox.projects) {
      toast({
        title: "No Create Option Selected.",
        description: "Please select at least one option to create.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    const result = await MySwal.fire({
      title: "Are you sure?",
      html: `
    <p>Do you want to proceed with the selected creation options?</p>
    <p><b>Database:</b> ${createCheckbox.database}</p>
    <p><b>Projects:</b> ${createCheckbox.projects}</p>
  `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, proceed!",
    });

    if (!result.isConfirmed) {
      return;
    }

    let response;
    // Creating client in the database
    if (createCheckbox.database) {
      try {
        response = await addClient(data, axiosPrivate);
        console.log("addClient response:", response);
        if (response?.status === 201) {
          toast({
            title: "Client Added Successfully.",
            description: response.data.success,
            status: "success",
            duration: 2000,
          });
          // Configure AWS SDK with your credentials
          AWS.config.update({
            region: AWS_REGION,
            accessKeyId: S3_ACCESS_KEY_ID,
            secretAccessKey: S3_SECRET_ACCESS_KEY,
          });
          // Create new client folder on S3 in client-creative directory
          const s3 = new AWS.S3();
          const params = {
            Bucket: S3_BUCKET_NAME,
            Key: `client-creative/${data.value}/`,
            Body: "", // Empty content, simulating a folder
          };

          try {
            const label = data.value;
            const _id = response.data.result._id;
            await s3.putObject(params).promise();
            handleCreateDirectory(label, _id);
          } catch (err) {
            console.error("Error creating folder:", err);
          }
        }
      } catch (err) {
        console.log(err);
        toast({
          title: "Error creating client in the database.",
          description: "An error has occurred.",
          status: "error",
          duration: 3000,
        });
      }
    }

    // Create new project for client
    if (createCheckbox.projects) {
      try {
        const projectData = {
          name: formData.label,
          taskLists: defaultNewClientTasks,
        };
        console.log("Creating client project");
        const [createProject] = await Promise.all([
          createClientProject(projectData),
        ]);
        console.log(createProject);
        if (response) {
          const updateClient = {
            _id: response.data.result._id,
            projectId: `${createProject}`,
          };
          handleUpdateClient(updateClient);
        }
        toast({
          title: "Client Project created Successfully.",
          description: response.data.success,
          status: "success",
          duration: 2000,
        });
      } catch (error) {
        toast({
          title: "Error creating new client project.",
          description: "An error has occurred.",
          status: "error",
          duration: 3000,
        });
      }
    }
  };

  const handleUpdateClient = async (data) => {
    console.log(data);
    const updatedField = {
      projectId: data?.projectId,
    };
    try {
      const response = await updateClient(updatedField, data._id);
      console.log(
        `successfully updated client project id: ${data?.projectId}`,
        response
      );
    } catch (err) {
      console.error(err);
    }
  };

  function camelCaseToTitleCase(camelCase) {
    const exceptions = ["SEO", "GA4", "OTT", "AI", "VLA"]; // Existing exceptions
    const specificTransformations = { coop: "CO-OP" }; // New specific transformations

    if (camelCase == null || camelCase === "") {
      return camelCase;
    }

    camelCase = camelCase.trim();
    let newText = "";
    for (let i = 0; i < camelCase.length; i++) {
      if (
        /[A-Z]/.test(camelCase[i]) &&
        i !== 0 &&
        /[a-z]/.test(camelCase[i - 1])
      ) {
        newText += " ";
      }
      newText += camelCase[i];
    }

    // Split the text and capitalize the first letter of each word
    let titleCaseText = newText
      .split(" ")
      .map((word) => {
        // Check if the word should be transformed specifically
        const upperWord = word.toUpperCase();
        if (specificTransformations[word.toLowerCase()]) {
          return specificTransformations[word.toLowerCase()];
        }
        // Check if the word is an exception, and format accordingly
        if (exceptions.includes(upperWord)) {
          return upperWord;
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");

    return titleCaseText;
  }

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    const checkboxId = checkboxState[name].id;

    setCheckboxState((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        status: checked,
      },
    }));

    const taskListUpdate = zohoTaskLists.find((task) => task.id === checkboxId);

    if (taskListUpdate && checked) {
      const updatedTask = { ...taskListUpdate, status: true };
      setDefaultNewClientTasks((prev) => {
        const exists = prev.some((item) => item.id === checkboxId);
        return exists ? prev : [...prev, updatedTask];
      });
    } else if (taskListUpdate && !checked) {
      setDefaultNewClientTasks((prev) =>
        prev.filter((item) => item.id !== checkboxId)
      );
    }
    setFormData((prev) => ({
      ...prev,
      products: {
        ...prev.products,
        [name]: {
          status: checked,
        },
      },
    }));
  };

  // Generate checkbox components from the state
  const checkboxes = Object.entries(checkboxState).map(([key, value]) => (
    <Checkbox
      key={key}
      name={key}
      isChecked={value.status}
      onChange={handleCheckboxChange}
      colorScheme="green"
      color={useColorModeValue("black", "white")}
    >
      {camelCaseToTitleCase(key.split(".").pop())}
    </Checkbox>
  ));

  const fetchTaskLists = async () => {
    try {
      const data = await getTaskList();
      const updatedTaskLists = data.task_list_templates.map((taskList) => ({
        id: taskList.id,
        name: taskList.name,
        status: false,
      }));
      setZohoTaskLists([...zohoTaskLists, ...updatedTaskLists]);
    } catch (error) {
      console.error("Failed to fetch Task Lists:", error);
    }
  };

  useEffect(() => {
    fetchTaskLists();
  }, []);

  return (
    <Box direction="column" pt={{ sm: "120px", md: "75px" }}>
      <Box mt="24px">
        <Card bg={bgColor} mb={"md"} withBorder>
          <Flex gap={2}>
            <Box
              width={"20vw"}
              border={"1px"}
              borderColor={"gray.200"}
              borderRadius={"md"}
            >
              <Select
                menuPortalTarget={document.body}
                isClearable
                isDisabled={isLoading}
                isLoading={isLoading}
                onChange={(newValue) => handleOnClick(newValue)}
                options={newOption}
                value={value}
                styles={colorStyles}
                placeholder="Search Clients..."
              />
            </Box>
            <Checkbox
              name={"database"}
              isChecked={createCheckbox.database}
              isDisabled={duplicates.length > 0 && !override}
              onChange={() =>
                setCreateCheckbox((prev) => ({
                  ...prev,
                  database: !createCheckbox.database,
                }))
              }
              colorScheme="green"
              color={useColorModeValue("black", "white")}
            >
              Database
            </Checkbox>
            <Checkbox
              name={"projects"}
              isChecked={createCheckbox.projects}
              onChange={() =>
                setCreateCheckbox((prev) => ({
                  ...prev,
                  projects: !createCheckbox.projects,
                }))
              }
              colorScheme="green"
              color={useColorModeValue("black", "white")}
            >
              Projects
            </Checkbox>
            <Button
              variant="danger"
              alignSelf="center"
              w="130px"
              h="35px"
              onClick={() => setSlowTransitionOpened(true)}
            >
              Override Database
            </Button>
            <Modal
              centered
              opened={slowTransitionOpened}
              onClose={() => setSlowTransitionOpened(false)}
              size={"md"}
              withCloseButton={false}
              closeOnClickOutside={false}
              title={
                <Text fontWeight={"bold"} fontSize={"large"}>
                  Are you sure?
                </Text>
              }
              overlayProps={{
                color:
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[9]
                    : theme.colors.gray[2],
                opacity: 0.55,
                blur: 3,
              }}
              styles={{
                header: {
                  color: "red",
                  backgroundColor: "#333",
                },
                content: {
                  color: "white",
                  backgroundColor: "#333",
                },
              }}
            >
              <Text pb={5}>
                This will override the Database and possibly create a duplicate
                client. Please verify the client isn't listed in the Client List
                or InActive Client List.
              </Text>
              <Group mt={"lg"} position="right">
                <Button
                  variant="outline"
                  color="green"
                  onClick={() => {
                    setOverride(true);
                    setSlowTransitionOpened(false);
                  }}
                >
                  Override
                </Button>
                <Button
                  variant="outline"
                  color="red"
                  onClick={() => {
                    setSlowTransitionOpened(false);
                  }}
                >
                  Cancel
                </Button>
              </Group>
            </Modal>
          </Flex>
          <CardHeader m="40px">
            <Flex direction="column" align={"center"}>
              <Text color={textColor} fontSize="lg" fontWeight="bold" mb="3px">
                Client Info
              </Text>
              <Text color="gray.400" fontWeight="normal" fontSize="sm">
                Mandatory Information
              </Text>
            </Flex>
          </CardHeader>
          <CardBody>
            <Flex direction="column" w="100%">
              <Stack direction="column" spacing="20px">
                <Stack direction="row" spacing={{ sm: "24px", lg: "30px" }}>
                  <FormControl>
                    <FormLabel
                      color={textColor}
                      fontWeight="bold"
                      fontSize="xs"
                    >
                      Client Name
                    </FormLabel>
                    <Input
                      variant="main"
                      placeholder="eg. Reliance Nissan"
                      fontSize="xs"
                      value={client ? client.Account_Name : formData.value}
                      onChange={handleLabelChange}
                      bg={useColorModeValue("white", "#343A40")}
                      color={textColor}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel
                      color={textColor}
                      fontWeight="bold"
                      fontSize="xs"
                    >
                      Client URL
                    </FormLabel>
                    <Input
                      variant="main"
                      placeholder="eg. https://www.reliancenissan.com/"
                      fontSize="xs"
                      name="clientwebsite"
                      value={
                        client ? client.Website : formData.clientwebsite || ""
                      }
                      onChange={handleChange}
                      bg={useColorModeValue("white", "#343A40")}
                      color={textColor}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel
                      color={textColor}
                      fontWeight="bold"
                      fontSize="xs"
                    >
                      Brand
                    </FormLabel>
                    <Input
                      variant="main"
                      placeholder="eg. Nissan"
                      fontSize="xs"
                      name="brand"
                      value={formData.brand}
                      onChange={handleChange}
                      bg={useColorModeValue("white", "#343A40")}
                      color={textColor}
                    />
                  </FormControl>
                </Stack>
                <CardHeader mb="10px">
                  <Flex direction="column" align={"center"}>
                    <Text
                      color={textColor}
                      fontSize="lg"
                      fontWeight="bold"
                      my="3px"
                    >
                      Products (New Client Start-up)
                    </Text>
                    <Text color="gray.400" fontWeight="normal" fontSize="sm">
                      Select the products the client is using
                    </Text>
                  </Flex>
                </CardHeader>
                <Grid templateColumns="repeat(5, 1fr)" gap={2}>
                  {checkboxes}
                </Grid>
              </Stack>
              <Heading size="sm" pt={3}>
                Task Lists to be Loaded upon client creation:
              </Heading>
              <Box
                border={"1px"}
                borderColor={"gray.200"}
                borderRadius={"md"}
                my={5}
                p={2}
              >
                <Wrap spacing={2}>
                  {defaultNewClientTasks.map((taskList) => (
                    <Tag
                      size={"md"}
                      key={taskList.id}
                      borderRadius={"md"}
                      variant={"solid"}
                      colorScheme={"red"}
                    >
                      <TagLabel>{taskList.name}</TagLabel>
                      <TagCloseButton />
                    </Tag>
                  ))}
                </Wrap>
              </Box>
            </Flex>
            <Button
              variant="primary"
              alignSelf="flex-end"
              mt="24px"
              w="100px"
              h="35px"
              isDisabled={client ? false : true}
              onClick={() => handleAddClient(formData)}
            >
              <Text fontSize="xs" color="#fff" fontWeight="bold">
                Create
              </Text>
            </Button>
          </CardBody>
        </Card>
        {duplicate ? (
          <Card pt="sm" bg={bgColor} mb="md" withBorder>
            <Box>
              <Box>
                Database Clients:{" "}
                {duplicates.map((client, index) => (
                  <Box key={index} p={1}>
                    <Tag
                      onClick={() => handleClick(client, index)} // Pass the index to the handler
                      style={{
                        cursor: "pointer",
                        backgroundColor:
                          selectedClientIndex === index ? "#3182ce" : "", // Only highlight the selected tag
                      }}
                    >
                      {client?.label}
                    </Tag>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box>
              <Box>
                CRM Clients:{" "}
                {zohoDuplicates.map((client, index) => (
                  <Box key={index} p={1}>
                    <Tag>{client?.Account_Name}</Tag>
                  </Box>
                ))}
              </Box>
            </Box>
          </Card>
        ) : null}
      </Box>
    </Box>
  );
}

export default NewClient;
